/**
 * @file useLabelPrintForm.ts
 * @description Состояние диалога печати этикеток (phase19.22, ui-design §1,
 *   §2, §5; W17-5a/5b/5c): отметки строк, количество, переопределение
 *   шаблона, «начать с N» по шаблонам, режим кодов, отправка и результат.
 *
 * Вынесено из диалога (SRP): диалог — раскладка, хук — правила. Главные:
 *   - все строки отмечены (решение пользователя 2026-09-25), кроме уже
 *     напечатанных в приёмке — они сняты, но доступны;
 *   - «начать с» по умолчанию — `nextStartAt` склада (продолжаем начатый лист);
 *   - сбой печати не сбрасывает выбор: «Повторить» шлёт то же самое.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { LabelPrintDialogItem, LabelPrintMode } from "./label-print-types";
import { buildLabelPrintInput } from "./label-print-request";
import { useLabelCutLines } from "./useLabelCutLines";
import { useSkuLabelTemplates } from "./useSkuLabelTemplates";
import { ApiError } from "../../../lib/api-client";
import {
  labelsApi,
  type LabelCodeMode,
  type LabelPrintResult,
  type LabelStartAt,
  type LabelTemplate,
} from "../../../lib/api-labels";
import { LABEL_MAX_TOTAL, summarizeSheets } from "../../../lib/label-sheets";
import { wmsErrorText } from "../../../lib/wms-error-text";

/** Строка формы. */
export interface LabelRow {
  key: string;
  item: LabelPrintDialogItem;
  checked: boolean;
  qty: number;
  /** Шаблон на эту печать; `null` — как у фасовки. */
  override: LabelTemplate | null;
}

/** Ошибка печати: `retryable` — сервис печати недоступен (502). */
export interface LabelPrintError {
  text: string;
  retryable: boolean;
}

function rowKey(item: LabelPrintDialogItem, index: number): string {
  return item.receiptItemId ?? item.lotId ?? `${item.skuId}#${index}`;
}

function initRows(mode: LabelPrintMode, items: LabelPrintDialogItem[]): LabelRow[] {
  return items.map((item, index) => ({
    key: rowKey(item, index),
    item,
    checked: !(mode === "receipt" && item.labelStatus === "PRINTED"),
    qty: item.defaultQty,
    override: null,
  }));
}

interface Params {
  open: boolean;
  mode: LabelPrintMode;
  items: LabelPrintDialogItem[];
  /** Меняется — строки собираются заново (смена партии в режиме `sku`). */
  resetKey: string;
  warehouseId: string;
  receiptId?: string;
  onPrinted?: (result: LabelPrintResult) => void;
}

export function useLabelPrintForm({ open, mode, items, resetKey, warehouseId, receiptId, onPrinted }: Params) {
  const [rows, setRows] = useState<LabelRow[]>([]);
  const [startAt, setStartAtState] = useState<LabelStartAt>({});
  const [codeMode, setCodeMode] = useState<LabelCodeMode>("BOTH");
  const [cutLines, setCutLines] = useLabelCutLines();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<LabelPrintError | null>(null);
  const [result, setResult] = useState<LabelPrintResult | null>(null);

  // Результат на экране — строки не пересобираем: вызывающий мог уже
  // перечитать отбор, и форма не должна «прыгнуть» под итогом печати.
  const showingResult = useRef(false);
  showingResult.current = result !== null;

  const wasOpen = useRef(false);

  // Открытие — чистый лист (без прошлого итога и ошибки); смена набора строк
  // при открытой форме — пересобрать строки, но не под итогом печати.
  useEffect(() => {
    const justOpened = open && !wasOpen.current;
    wasOpen.current = open;
    if (!open) return;
    if (justOpened) {
      setResult(null);
      setError(null);
    } else if (showingResult.current) {
      return;
    }
    setRows(initRows(mode, items));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- пересборка только по resetKey
  }, [open, resetKey]);

  // Калибровка склада: «начать с» продолжает начатый лист, режим кодов — дефолт склада.
  useEffect(() => {
    if (!open || !warehouseId) return;
    let cancelled = false;
    labelsApi
      .labelSheets(warehouseId)
      .then((res) => {
        if (cancelled) return;
        setStartAtState(Object.fromEntries(res.sheets.map((s) => [s.template, s.nextStartAt])));
        setCodeMode(res.defaultCodeMode);
      })
      .catch(() => {
        // Без калибровки печать работает: «начать с 1», коды — оба.
        if (!cancelled) setStartAtState({});
      });
    return () => {
      cancelled = true;
    };
  }, [open, warehouseId]);

  // Шаблон по фасовке спрашиваем только в режиме `sku` (одна фасовка): в
  // приёмке и остатках он уже пришёл в строке (`SkuPrintInfo`).
  const unknown = useMemo(
    () => (mode === "sku" ? items.filter((i) => !i.template).map((i) => i.skuId) : []),
    [mode, items],
  );
  const templates = useSkuLabelTemplates(unknown, open && mode === "sku");

  const templateOf = useCallback(
    (row: LabelRow): LabelTemplate =>
      row.override ?? row.item.template ?? templates.templateOf(row.item.skuId) ?? "STANDARD",
    [templates],
  );

  const checked = rows.filter((r) => r.checked && r.qty > 0);
  const summary = summarizeSheets(
    checked.map((r) => ({ template: templateOf(r), quantity: r.qty })),
    startAt,
  );
  const overLimit = summary.totalLabels > LABEL_MAX_TOTAL;

  const patchRow = (key: string, patch: Partial<LabelRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  async function submit(): Promise<void> {
    if (checked.length === 0 || submitting || overLimit) return;
    setSubmitting(true);
    setError(null);
    const input = buildLabelPrintInput({
      mode,
      warehouseId,
      receiptId,
      codeMode,
      cutLines,
      startAt,
      rows: checked.map((r) => ({ item: r.item, qty: r.qty, override: r.override, template: templateOf(r) })),
    });
    try {
      const res = await labelsApi.print(input);
      setResult(res);
      // «Напечатать ещё» продолжит начатые листы с того места, где закончили.
      setStartAtState((prev) => ({
        ...prev,
        ...Object.fromEntries(res.perTemplate.map((p) => [p.template, p.nextStartAt])),
      }));
      onPrinted?.(res);
    } catch (err) {
      setError({
        text: wmsErrorText(err, "Не удалось сформировать этикетки"),
        retryable: err instanceof ApiError && err.code === "label_render_failed",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return {
    rows,
    templateOf,
    templatesLoading: templates.loading,
    toggle: (key: string) => {
      const row = rows.find((r) => r.key === key);
      if (row) patchRow(key, { checked: !row.checked });
    },
    setAll: (value: boolean) => setRows((prev) => prev.map((r) => ({ ...r, checked: value }))),
    setQty: (key: string, qty: number) => patchRow(key, { qty }),
    setOverride: (key: string, override: LabelTemplate | null) => patchRow(key, { override }),
    startAt,
    setStartAt: (template: LabelTemplate, value: number) =>
      setStartAtState((prev) => ({ ...prev, [template]: value })),
    codeMode,
    setCodeMode,
    cutLines,
    setCutLines,
    summary,
    overLimit,
    canSubmit: checked.length > 0 && !overLimit && !submitting,
    submitting,
    error,
    result,
    submit,
    /** «Напечатать ещё» — вернуться к форме с тем же выбором. */
    backToForm: () => setResult(null),
  };
}

export type LabelPrintForm = ReturnType<typeof useLabelPrintForm>;
